/**
 * "Thêm TLD" (`POST /registry/tld-setup`): one dialog, one request. Staff choose the
 * registration connection, the TLD (from the connection's own list when it publishes one), one
 * registry cost applied to register / renew / restore / transfer (each editable), and the
 * selling price as a margin or a fixed price per year. The TLD class's default add-ons and the
 * RDAP / WHOIS publication are set automatically (shown, not asked); the mandatory add-ons that
 * need a cost (the ICANN fee of a direct gTLD) ask for it. One reason for the audit log.
 */

import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { TldSetupBody, TldSetupOperation, TldSetupOptions } from '../../api/areas/registry';
import type { WriteOptions } from '../../api/types';
import { Badge } from '../../components/Badge';
import { Callout, Field } from '../../components/blocks';
import { Stepper, type StepState } from '../../components/flow';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { PublicationText, publisherOf, RegistryProblemCallout, tldUrl } from './readiness';
import { enumLabel } from './shared';

type TFn = (key: string, o?: Record<string, unknown>) => string;
const OPS: TldSetupOperation[] = ['REGISTER', 'RENEW', 'RESTORE', 'TRANSFER'];
const CURRENCIES = ['USD', 'VND', 'EUR'];

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <fieldset className="grid gap-3 rounded-lg border p-3">
      <legend className="px-1 text-sm font-semibold">
        <span className="mr-1.5 inline-flex size-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{n}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function SetupForm({ close, changed, connectionId: fixedConnection }: DialogBodyProps & { connectionId?: string }) {
  const { t, i18n } = useTranslation('registry');
  const tr = t as unknown as TFn;
  const connections = useApi((signal) => api.listConnections({}, { signal }), []);
  const [connectionId, setConnectionId] = useState(fixedConnection ?? '');
  const [search, setSearch] = useState('');
  const options = useApi(
    (signal) => (connectionId ? api.getTldSetupOptions({ connection_id: connectionId, q: search.trim() || undefined, limit: 30 }, { signal }) : Promise.resolve(null as TldSetupOptions | null)),
    [connectionId, search],
  );
  const [zone, setZone] = useState('');
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [perOp, setPerOp] = useState(false);
  const [opCost, setOpCost] = useState<Partial<Record<TldSetupOperation, string>>>({});
  const [saleMode, setSaleMode] = useState<'MARGIN_PERCENT' | 'FIXED_PRICE'>('MARGIN_PERCENT');
  const [sale, setSale] = useState('');
  const [addonCosts, setAddonCosts] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const opt = options.status === 'success' ? options.data : null;
  const picked = opt?.zones.find((z) => z.zone === zone) ?? null;
  const connection = (connections.status === 'success' ? connections.data.items : []).find((c) => c.id === connectionId);
  const bookCurrency = opt?.books[0]?.currency ?? 'VND';
  const needCost = (picked?.addons ?? []).filter((a) => a.needs_cost);
  const addonCost = (code: string) => addonCosts[code] ?? needCost.find((a) => a.code === code)?.suggested_cost ?? '';
  const makeBody = (): TldSetupBody | null => {
    if (!picked || !cost.trim() || !sale.trim()) return null;
    const per: Partial<Record<TldSetupOperation, string>> = {};
    if (perOp) for (const op of OPS) if (opCost[op] !== undefined && opCost[op] !== cost) per[op] = opCost[op] ?? '';
    return {
      zone: picked.zone,
      connection_id: connectionId,
      cost: { amount: cost.trim(), currency, per_operation: per },
      sale: { mode: saleMode, value: sale.trim() },
      addon_costs: Object.fromEntries(needCost.map((a) => [a.code, addonCost(a.code)])),
      addon_currency: needCost.find((a) => a.code === 'ICANN_FEE')?.suggested_currency ?? undefined,
      reason: reason.trim(),
    };
  };
  const body = makeBody();
  const w = useWrite((b: TldSetupBody, opts: WriteOptions) => api.setupTld(b, opts));
  const done = w.state.phase === 'done' ? w.state.result : null;
  if (done) {
    return (
      <div className="grid gap-4">
        <Callout tone="ok" title={t('setup.created', { zone: done.zone })}>
          <Link to={tldUrl(done.tld_id)} className="font-medium text-primary underline-offset-4 hover:underline" onClick={close}>
            {t('setup.openTld', { zone: done.zone })}
          </Link>
        </Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  const upstream = connection?.kind === 'UPSTREAM_REGISTRAR';
  // Where the form is: each step done once its value is given, the first open one current.
  const filled = [!!connectionId, !!picked, !!cost.trim(), !!sale.trim(), !!picked, !!body && !!reason.trim()];
  const firstOpen = filled.indexOf(false);
  const stepState = (i: number): StepState => (filled[i] ? 'done' : i === firstOpen ? 'current' : 'todo');
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (body) void w.run(body).then((r) => r && changed());
      }}
    >
      <p className="text-sm text-muted-foreground">{t('setup.description')}</p>
      <Stepper
        className="hidden md:flex"
        label={t('setup.title')}
        steps={(['connection', 'zone', 'cost', 'sale', 'addons', 'create'] as const).map((k, i) => ({ id: k, label: t(`hub.step.${k}`), state: stepState(i) }))}
      />
      <Step n={1} title={t('setup.connection')}>
        <Field label={t('setup.connection')} hint={t('setup.connectionHint')}>
          <select
            className={selectClass}
            value={connectionId}
            onChange={(e) => {
              setConnectionId(e.target.value);
              setZone('');
            }}
            required
          >
            <option value="">{t('setup.choose')}</option>
            {(connections.status === 'success' ? connections.data.items : [])
              .filter((c) => c.status !== 'RETIRED')
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </option>
              ))}
          </select>
        </Field>
      </Step>
      {connectionId ? (
        <Step n={2} title={t('setup.zone')}>
          <Field label={t('setup.zoneSearch')}>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="com, net, shop…" autoComplete="off" />
          </Field>
          {options.status === 'loading' ? <Loading rows={2} /> : null}
          {opt ? (
            <>
              <p className="text-xs text-muted-foreground">{opt.source === 'CONNECTION' ? t('setup.fromConnection', { name: opt.connection.name }) : t('setup.fromCatalog')}</p>
              {opt.list_problem ? <RegistryProblemCallout problem={opt.list_problem} /> : null}
              {opt.zones.length ? (
                <ul className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                  {opt.zones.map((z) => (
                    <li key={z.zone}>
                      <button
                        type="button"
                        onClick={() => setZone(z.zone)}
                        className={`rounded-md border px-2 py-1 font-mono text-sm ${zone === z.zone ? 'border-primary bg-accent' : ''}`}
                        aria-pressed={zone === z.zone}
                      >
                        .{z.unicode}
                        {z.status === 'ACTIVE' ? <span className="ml-1 font-sans text-xs text-muted-foreground">({t('setup.selling')})</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t('setup.noZone')}</p>
              )}
            </>
          ) : null}
        </Step>
      ) : null}
      {picked ? (
        <>
          <Step n={3} title={t('setup.cost')}>
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('setup.cost')} hint={t('setup.costHint')}>
                <Input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" required className="w-32" />
              </Field>
              <Field label={t('setup.currency')}>
                <select className={selectClass} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={perOp} onChange={(e) => setPerOp(e.target.checked)} />
              {t('setup.perOperation')}
            </label>
            {perOp ? (
              <div className="grid gap-2 sm:grid-cols-4">
                {OPS.map((op) => (
                  <Field key={op} label={tr(`setup.op.${op}`)}>
                    <Input value={opCost[op] ?? cost} onChange={(e) => setOpCost((m) => ({ ...m, [op]: e.target.value }))} inputMode="decimal" />
                  </Field>
                ))}
              </div>
            ) : null}
          </Step>
          <Step n={4} title={t('setup.sale')}>
            {opt?.books.length ? (
              <p className="text-xs text-muted-foreground">{t('setup.saleHint', { books: opt.books.map((b) => `${b.code} (${b.currency})`).join(', ') })}</p>
            ) : (
              <Callout tone="warn">{t('setup.noBooks')}</Callout>
            )}
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" name="sale-mode" checked={saleMode === 'MARGIN_PERCENT'} onChange={() => setSaleMode('MARGIN_PERCENT')} />
                {t('setup.margin')}
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="sale-mode" checked={saleMode === 'FIXED_PRICE'} onChange={() => setSaleMode('FIXED_PRICE')} />
                {t('setup.fixed', { currency: bookCurrency })}
              </label>
            </div>
            <Input value={sale} onChange={(e) => setSale(e.target.value)} inputMode="decimal" required className="w-40" aria-label={t('setup.sale')} />
          </Step>
          <Step n={5} title={t('setup.addons')}>
            {picked.addons.length ? (
              <ul className="grid gap-1.5 text-sm">
                {picked.addons.map((a) => (
                  <li key={a.code} className="flex flex-wrap items-center gap-2">
                    <span>{i18n.language === 'vi' ? a.name_vi : a.name_en}</span>
                    <Badge tone={a.mode === 'MANDATORY' ? 'info' : 'neutral'} icon={false}>
                      {tr(`setup.mode.${a.mode}`, { defaultValue: a.mode })}
                    </Badge>
                    {a.needs_cost ? (
                      <Field label={t('setup.addonCost', { name: i18n.language === 'vi' ? a.name_vi : a.name_en })}>
                        <span className="flex items-center gap-1">
                          <Input value={addonCost(a.code)} onChange={(e) => setAddonCosts((m) => ({ ...m, [a.code]: e.target.value }))} inputMode="decimal" className="w-24" required />
                          <span className="text-xs text-muted-foreground">{a.suggested_currency ?? currency}</span>
                        </span>
                      </Field>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('setup.addonsNone')}</p>
            )}
            {upstream && (picked.tld_class === 'GTLD' || picked.tld_class === 'NEW_GTLD') ? <p className="text-xs text-muted-foreground">{t('setup.leftOut', { name: connection?.name ?? '' })}</p> : null}
            <p className="flex flex-wrap items-center gap-2 text-sm">
              {t('setup.publication')}: <PublicationText publisher={publisherOf(picked.rdap_enabled, picked.whois_enabled, connection?.kind, picked.tld_class)} connection={connection} />
            </p>
          </Step>
          <Step n={6} title={t('setup.create')}>
            <p className="text-sm">
              {t('setup.summary', {
                zone: picked.unicode,
                connection: connection?.name ?? '',
                cost: cost || '—',
                currency,
                sale: saleMode === 'MARGIN_PERCENT' ? t('setup.summaryMargin', { value: sale || '—' }) : t('setup.summaryFixed', { value: sale || '—', currency: bookCurrency }),
              })}{' '}
              <span className="text-muted-foreground">({enumLabel('tldClass', picked.tld_class)})</span>
            </p>
            <ReasonField value={reason} onChange={setReason} />
          </Step>
        </>
      ) : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!body || !reason.trim() || !opt?.books.length} submit={t('setup.create')} onClose={close} />
    </form>
  );
}

/** The "Thêm TLD" button (TLD list, TLD catalogue, registration channels); `?action=add-tld` on
 * the TLD list opens it (Ctrl-K "Thiết lập TLD mới"). */
export function TldSetupButton({ onChanged, connectionId, defaultOpen }: { onChanged?: () => void; connectionId?: string; defaultOpen?: boolean }) {
  const { t } = useTranslation('registry');
  return (
    <ActionDialog label={t('setup.button')} icon={Plus} variant="default" title={t('setup.title')} wide onChanged={onChanged} defaultOpen={defaultOpen}>
      {(p) => <SetupForm {...p} connectionId={connectionId} />}
    </ActionDialog>
  );
}
